import { Button, Card, Chip, Stack, Typography } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { BasicDialog } from '../../../../components/Dialog/BasicDialog';
import { FieldSelect } from '../../../../components/Form/Display/FieldSelect';
import { StickyFormButtons } from '../../../../components/Form/StickyFormButtons';
import { MaxModalFormWidth, ModalFormWidth } from '../../../../constants/styles';
import { CaptableType, ICaptableDataModel } from '../../../../data-models/captable2.data-model';
import { RendererType } from '../../../../data-models/field-definition';
import { ISelectMeta } from '../../../../data-models/field3.data-model';
import { PermissionKey } from '../../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../../services/PermissionService';
import {
  captablesByCompanyStateJ,
  captableVersionsByCompanyStateJ,
  selectedCaptableIdStateJ,
  selectedCaptableStateJ,
} from '../../../../services/state/CapTableDataStateJ';
import { ICaptableVersionGroup } from '../../../../services/queries/MaggieCapTableQueries';
import { cardStyles } from '../../../../theme/component-styles';
import { MaggieFeatureFlags } from '../../../../util/feature-flags';
import { FormatterService } from '../../../../util/formatter-service';
import { DateFormattersConfig } from '../../../../util/formatters/DateFormatters';
import { createFormField } from '../../../../view-models/form.view-model';
import { InfoBox } from '../../../Mappings/forms/InfoBox';
import { selectedCompanyIdProfileAtom } from '../../state/UIStateJ';
import {
  CaptableSourceMap,
  formatCaptableVersionOption,
  getCaptablePrimarySourceKey,
  getCaptableSourceKey,
  getCaptableSourceLabel,
} from '../captable-utils';
import { useMakeCaptablePrimary, useMakeCaptablePrimaryLegacy } from '../CapTableActions';
import { CaptableAsOfDateLegacy } from './CaptableAsOfDate';
import { CaptableSourceLogo } from './CaptableSourceLogo';
import { VersionClassificationInfo } from './VersionClassification';

export function useCaptableSelectorField() {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const captablesForCompany = useAtomValue(captablesByCompanyStateJ(companyId ?? -1));

  return useMemo(
    () =>
      createFormField<ISelectMeta<number>>({
        key: 'selectedCaptable',
        label: '',
        dataType: 'string',
        renderer: RendererType.singleSelect,
        rendererMeta: {
          values: (captablesForCompany ?? []).map((opt, i) => {
            const source = getCaptableSourceLabel(opt);
            return {
              value: opt.id,
              displayName: `Cap Table #${i + 1} - ${source}`,
            };
          }),
          multi: false,
        },
        disableClearable: true,
        // Pick-lists, not search boxes — don't highlight the value text on focus.
        selectOnFocus: false,
      }),
    [captablesForCompany]
  );
}

/** Version picklist field for the currently-selected source group (FD-3733). */
export function useCaptableVersionField(group: ICaptableVersionGroup | undefined) {
  return useMemo(
    () =>
      createFormField<ISelectMeta<number>>({
        key: 'selectedVersion',
        label: '',
        dataType: 'string',
        renderer: RendererType.singleSelect,
        rendererMeta: {
          // Rows are label + date only; review/restated state lives in the chips beside the selector.
          values: (group?.versions ?? []).map((version) => ({
            value: version.id,
            displayName: formatCaptableVersionOption(version),
          })),
          multi: false,
        },
        disableClearable: true,
        // Pick-lists, not search boxes — don't highlight the value text on focus.
        selectOnFocus: false,
      }),
    [group]
  );
}

function SelectorField({ label, width, children }: PropsWithChildren<{ label: string; width: string }>) {
  return (
    <Stack gap={'0.25rem'} sx={{ width, '& .MuiAutocomplete-root': { width: '100%' } }}>
      <Typography variant={'caption'} color={'text.secondary'}>
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

export const CaptableSelector: FC = () => {
  const { asOfCapTables } = useFlags<MaggieFeatureFlags>();
  return asOfCapTables ? <VersionedCaptableSelector /> : <LegacyCaptableSelector />;
};

const LegacyCaptableSelector: FC = () => {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const captablesForCompany = useAtomValue(captablesByCompanyStateJ(companyId ?? -1));

  const [selectedCaptableId, setSelectedCaptableId] = useAtom(selectedCaptableIdStateJ(companyId ?? -1));
  const selectedCaptable = useAtomValue(selectedCaptableStateJ(companyId ?? -1));

  const selector = useCaptableSelectorField();

  if (!captablesForCompany) {
    return null;
  }

  return (
    <Stack
      display={'grid'}
      gridTemplateColumns={'15rem 2rem auto auto auto'}
      justifyContent={'start'}
      alignItems={'center'}
      gap={'1rem'}
    >
      <FieldSelect
        formField={selector}
        formProps={
          {
            onChange: (val) => setSelectedCaptableId(val),
            value: selectedCaptableId,
          } as ControllerRenderProps
        }
      />
      <MoreCaptableInfo captable={selectedCaptable} />
    </Stack>
  );
};

const VersionedCaptableSelector: FC = () => {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const captablesForCompany = useAtomValue(captablesByCompanyStateJ(companyId ?? -1));
  const versionGroups = useAtomValue(captableVersionsByCompanyStateJ(companyId ?? -1));

  const [selectedCaptableId, setSelectedCaptableId] = useAtom(selectedCaptableIdStateJ(companyId ?? -1));
  const selectedCaptable = useAtomValue(selectedCaptableStateJ(companyId ?? -1));

  // The active source group is the timeline containing the selected version.
  const activeGroup = useMemo(
    () => (versionGroups ?? []).find((g) => g.versions.some((v) => v.id === selectedCaptableId)) ?? null,
    [versionGroups, selectedCaptableId]
  );
  const activeSourceKey = activeGroup ? getCaptableSourceKey(activeGroup) : null;
  // The source dropdown tracks the source, so bind it to that source's current cap table id even when
  // a historical version is selected.
  const sourceValue = useMemo(
    () =>
      (captablesForCompany ?? []).find((c) => getCaptableSourceKey(c) === activeSourceKey)?.id ??
      selectedCaptableId,
    [captablesForCompany, activeSourceKey, selectedCaptableId]
  );

  const sourceField = useCaptableSelectorField();
  const versionField = useCaptableVersionField(activeGroup ?? undefined);
  const versionCount = activeGroup?.versions.length ?? 0;
  const selectedVersion = useMemo(
    () => activeGroup?.versions.find((v) => v.id === selectedCaptableId) ?? null,
    [activeGroup, selectedCaptableId]
  );

  if (!captablesForCompany) {
    return null;
  }

  return (
    <Stack direction={'row'} alignItems={'flex-end'} gap={'1rem'} flexWrap={'wrap'}>
      <SelectorField label={'Cap Table Source'} width={'18rem'}>
        <FieldSelect
          formField={sourceField}
          formProps={
            { onChange: (val) => setSelectedCaptableId(val), value: sourceValue } as ControllerRenderProps
          }
        />
      </SelectorField>
      <SelectorField
        label={`Version History (${versionCount} version${versionCount === 1 ? '' : 's'})`}
        width={'18rem'}
      >
        <FieldSelect
          formField={versionField}
          formProps={
            {
              onChange: (val) => setSelectedCaptableId(val),
              value: selectedCaptableId,
            } as ControllerRenderProps
          }
        />
      </SelectorField>
      <Stack direction={'row'} alignItems={'center'} gap={'0.75rem'} flexWrap={'wrap'} pb={'0.25rem'}>
        <VersionClassificationInfo version={selectedVersion} companyId={companyId ?? -1} />
        <MoreCaptableInfo captable={selectedCaptable} />
      </Stack>
    </Stack>
  );
};

function LastUpdate({ captable }: { captable: ICaptableDataModel }) {
  const dateFormatter = FormatterService.get().getFormatterForModel(DateFormattersConfig.date);
  if (!captable.updatedAt) {
    return null;
  }
  return (
    <Typography variant={'body2'} color='text.secondary'>
      {`Last Update: ${dateFormatter(captable.updatedAt)}`}
    </Typography>
  );
}

export function MoreCaptableInfo({ captable }: { captable: ICaptableDataModel | null }) {
  const { asOfCapTables, showCapTableAsOfDate } = useFlags<MaggieFeatureFlags>();
  if (!captable) {
    return null;
  }

  if (asOfCapTables) {
    return (
      <>
        <CaptableSourceLogo captable={captable} key={captable.id} />
        {captable?.primary ? <Chip label='Primary' color='primary' /> : <MakePrimary captable={captable} />}
        <LastUpdate captable={captable} />
      </>
    );
  }

  return (
    <>
      <CaptableSourceLogo captable={captable} key={captable.id} />
      {captable?.primary ? (
        <Chip label='Primary' color='primary' />
      ) : (
        <MakePrimaryLegacy captable={captable} />
      )}
      {showCapTableAsOfDate && captable.type === CaptableType.user && (
        <CaptableAsOfDateLegacy captable={captable} />
      )}
      <LastUpdate captable={captable} />
    </>
  );
}

function MakePrimaryLegacy({ captable }: { captable: ICaptableDataModel | null }) {
  const makePrimary = useMakeCaptablePrimaryLegacy();
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const canPromoteCaptable = PermissionService.get().hasPermission(PermissionKey.canPromoteCaptable);

  const onConfirm = useCallback(async () => {
    if (!captable) {
      return;
    }
    setLoading(true);
    await makePrimary(captable.id);
    setLoading(false);
    setShowConfirm(false);
  }, [makePrimary, captable]);

  if (!captable || !canPromoteCaptable) {
    return null;
  }
  return (
    <>
      {showConfirm && (
        <MakePrimaryDialog
          captable={captable}
          onConfirm={onConfirm}
          loading={loading}
          onClose={() => setShowConfirm(false)}
        />
      )}
      <Button color='secondary' onClick={() => setShowConfirm(true)}>
        Make Primary
      </Button>
    </>
  );
}

export function MakePrimary({ captable }: { captable: ICaptableDataModel | null }) {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const makePrimary = useMakeCaptablePrimary();
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const canPromoteCaptable = PermissionService.get().hasPermission(PermissionKey.canPromoteCaptable);

  const onConfirm = useCallback(async () => {
    if (!captable) {
      return;
    }
    setLoading(true);
    await makePrimary(companyId, getCaptablePrimarySourceKey(captable));
    setLoading(false);
    setShowConfirm(false);
  }, [makePrimary, captable, companyId]);

  if (!captable || !canPromoteCaptable) {
    return null;
  }
  return (
    <>
      {showConfirm && (
        <MakePrimaryDialog
          captable={captable}
          onConfirm={onConfirm}
          loading={loading}
          onClose={() => setShowConfirm(false)}
        />
      )}
      <Button color='secondary' onClick={() => setShowConfirm(true)}>
        Make Primary
      </Button>
    </>
  );
}

function MakePrimaryDialog({
  captable,
  onConfirm,
  loading,
  onClose,
}: {
  captable: ICaptableDataModel;
  onConfirm: () => void;
  loading: boolean;
  onClose: () => void;
}) {
  return (
    <BasicDialog
      titleComponent={<Typography>Make This Cap Table Primary</Typography>}
      open={true}
      onClose={onClose}
    >
      <Stack
        style={{ padding: '0.25rem 2.5rem 0', width: ModalFormWidth, maxWidth: MaxModalFormWidth }}
        gap='0.5rem'
      >
        <Card
          sx={{ ...cardStyles, display: 'grid', gridAutoFlow: 'column', justifyContent: 'space-between' }}
        >
          <Typography variant='body2'>{`${CaptableSourceMap[captable.source]} Cap Table`}</Typography>
          <LastUpdate captable={captable} />
        </Card>

        <InfoBox
          variant='warning'
          message={`Data from this Cap Table source will be used by default across the Foresight platform. Are you sure you want to make this change?`}
        />
      </Stack>
      <StickyFormButtons
        onSubmit={onConfirm}
        onCancel={onClose}
        submitLabel={'Make Primary'}
        loading={loading}
      />
    </BasicDialog>
  );
}
